/**
 * /safety/training/courses/new and /safety/training/courses/:courseId/edit —
 * a training course's form on its own page. It was a slide-over drawer on
 * Course Management until split panes and drawers beside a list were banned
 * (CLAUDE.md). Save sits on the band, top right; it sends what the drawer sent
 * (POST for a new course, PUT for an edit, under the API's own keys) and
 * returns to the list as it was left.
 *
 * The edit page reads its course out of the Course Management list query: the
 * API has no read of one course (see trainingCourses.ts), so a course the list
 * does not hold is "not found", and a list that did not load is a failure, not
 * a blank form.
 */
import React, { useState } from 'react';
import { useParams } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';
import { Loader2, Save } from 'lucide-react';
import { toast } from 'sonner';
import { api } from '../../api/client';
import { PageHeaderBand } from '../../components/layout/PageHeaderBand';
import { LoadFailureNotice } from '../../components/ui/LoadFailureNotice';
import { queryFailureError, queryLoadFailure } from '../../lib/load-failure';
import { useBackToList } from '../downhole/recordPage';
import { TRAINING_COURSES_KEY, useTrainingCourses, type Course } from './trainingCourses';

const LIST = '/safety/training/courses';
const PANEL = 'rounded-fi-panel border border-fieldiq-border bg-fieldiq-card';

/** The drawer's fields, its save and its payload, unchanged; the caller places the button on the band. */
function useCourseForm({ course, onSaved }: { course: Course | null; onSaved: () => void }) {
  const [title, setTitle] = useState(course?.title ?? '');
  const [description, setDescription] = useState(course?.description ?? '');
  const [videoUrl, setVideoUrl] = useState(course?.video_url ?? '');
  const [passingScore, setPassingScore] = useState(course?.passing_score ?? 80);
  // 36 is the API's own default for a new course (POST /training/courses).
  const [validityMonths, setValidityMonths] = useState(course?.validity_months ?? 36);
  const [maxAttempts, setMaxAttempts] = useState(course?.max_attempts_per_day ?? 3);
  const [questionsPerQuiz, setQuestionsPerQuiz] = useState(course?.question_pool_size ?? 10);
  const [saving, setSaving] = useState(false);

  const handleSave = async () => {
    if (!title.trim()) { toast.error('Title is required'); return; }
    setSaving(true);
    try {
      const payload = {
        title: title.trim(),
        description: description.trim() || null,
        video_url: videoUrl.trim() || null,
        passing_score: passingScore,
        validity_months: validityMonths,
        max_attempts_per_day: maxAttempts,
        question_pool_size: questionsPerQuiz,
      };
      if (course) {
        await api.safety.updateTrainingCourse(course.id, payload);
        toast.success('Course updated');
      } else {
        await api.safety.createTrainingCourse(payload);
        toast.success('Course created');
      }
      onSaved();
    } catch {
      toast.error('Failed to save course');
    } finally {
      setSaving(false);
    }
  };

  return {
    save: (
      <button
        type="button"
        onClick={handleSave}
        disabled={saving}
        className="fi-action fi-action--standard fi-action--primary disabled:cursor-not-allowed disabled:opacity-50"
      >
        {saving ? <Loader2 className="size-4 animate-spin" aria-hidden="true" /> : <Save className="size-4" aria-hidden="true" />}
        {saving ? 'Saving...' : course ? 'Update Course' : 'Create Course'}
      </button>
    ),
    form: (
      <div className="grid gap-x-8 gap-y-5 p-4 lg:grid-cols-2">
        {/* Title */}
        <div className="space-y-1.5">
          <label htmlFor="course-title" className="text-xs font-medium text-muted-foreground">Title</label>
          <input
            id="course-title"
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            className="w-full h-9 px-3 bg-background border border-border rounded-lg text-foreground text-sm focus:outline-none focus:border-primary/50"
            placeholder="e.g. H2S Safety Awareness"
          />
        </div>

        {/* Video URL */}
        <div className="space-y-1.5">
          <label htmlFor="course-video" className="text-xs font-medium text-muted-foreground">Video URL</label>
          <input
            id="course-video"
            value={videoUrl}
            onChange={(e) => setVideoUrl(e.target.value)}
            className="w-full h-9 px-3 bg-background border border-border rounded-lg text-foreground text-sm focus:outline-none focus:border-primary/50"
            placeholder="YouTube embed URL or direct video link"
          />
          <p className="text-[10px] text-muted-foreground">YouTube embed URL or direct video link</p>
        </div>

        {/* Description */}
        <div className="space-y-1.5 lg:col-span-2">
          <label htmlFor="course-description" className="text-xs font-medium text-muted-foreground">Description</label>
          <textarea
            id="course-description"
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            rows={3}
            className="w-full px-3 py-2 bg-background border border-border rounded-lg text-foreground text-sm focus:outline-none focus:border-primary/50 resize-none"
            placeholder="Course description..."
          />
        </div>

        {/* Passing Score */}
        <div className="space-y-1.5">
          <label htmlFor="course-passing" className="text-xs font-medium text-muted-foreground">Passing Score</label>
          <div className="flex items-center gap-2">
            <input
              id="course-passing"
              type="number"
              value={passingScore}
              onChange={(e) => setPassingScore(Number(e.target.value))}
              min={1}
              max={100}
              className="w-24 h-9 px-3 bg-background border border-border rounded-lg text-foreground text-sm focus:outline-none focus:border-primary/50"
            />
            <span className="text-sm text-muted-foreground">%</span>
          </div>
        </div>

        {/* Validity Period */}
        <div className="space-y-1.5">
          <label htmlFor="course-validity" className="text-xs font-medium text-muted-foreground">Validity Period</label>
          <select
            id="course-validity"
            value={validityMonths}
            onChange={(e) => setValidityMonths(Number(e.target.value))}
            className="w-full h-9 px-3 bg-background border border-border rounded-lg text-foreground text-sm focus:outline-none focus:border-primary/50"
          >
            <option value={12}>1 Year</option>
            <option value={24}>2 Years</option>
            <option value={36}>3 Years</option>
          </select>
        </div>

        {/* Max Attempts Per Day */}
        <div className="space-y-1.5">
          <label htmlFor="course-attempts" className="text-xs font-medium text-muted-foreground">Max Attempts Per Day</label>
          <input
            id="course-attempts"
            type="number"
            value={maxAttempts}
            onChange={(e) => setMaxAttempts(Number(e.target.value))}
            min={1}
            max={10}
            className="block w-24 h-9 px-3 bg-background border border-border rounded-lg text-foreground text-sm focus:outline-none focus:border-primary/50"
          />
        </div>

        {/* Questions Per Quiz */}
        <div className="space-y-1.5">
          <label htmlFor="course-pool" className="text-xs font-medium text-muted-foreground">Questions Per Quiz</label>
          <input
            id="course-pool"
            type="number"
            value={questionsPerQuiz}
            onChange={(e) => setQuestionsPerQuiz(Number(e.target.value))}
            min={1}
            max={50}
            className="block w-24 h-9 px-3 bg-background border border-border rounded-lg text-foreground text-sm focus:outline-none focus:border-primary/50"
          />
        </div>
      </div>
    ),
  };
}

/** The form and its band; mounted once the course is known, so the fields start from its values. */
function CourseFormPage({ course, onBack }: { course: Course | null; onBack: () => void }) {
  const queryClient = useQueryClient();
  const { save, form } = useCourseForm({
    course,
    onSaved: () => {
      queryClient.invalidateQueries({ queryKey: TRAINING_COURSES_KEY });
      onBack();
    },
  });
  return (
    <>
      <PageHeaderBand
        eyebrow="Training course"
        title={course ? 'Edit Course' : 'New Course'}
        onBack={onBack}
        backLabel="Back to courses"
        actions={save}
      />
      <Body>
        <section className={PANEL} data-testid="course-form">
          {form}
        </section>
      </Body>
    </>
  );
}

function Body({ children }: { children: React.ReactNode }) {
  return (
    <section className="px-[var(--fi-page-inline)] py-[var(--fi-page-block)] pb-[var(--bottom-chrome)]">
      <div className="mx-auto max-w-[1920px] space-y-4">{children}</div>
    </section>
  );
}

export default function CourseEditScreen() {
  const { courseId } = useParams<{ courseId: string }>();
  const back = useBackToList(LIST);
  const isNew = !courseId;
  const query = useTrainingCourses();
  const failure = queryLoadFailure(query);
  const course = courseId ? query.data?.find((c) => c.id === courseId) ?? null : null;

  let content: React.ReactNode;
  if (isNew) {
    content = <CourseFormPage course={null} onBack={back} />;
  } else if (course) {
    content = <CourseFormPage key={course.id} course={course} onBack={back} />;
  } else {
    const state = query.isLoading ? (
      <div className="flex justify-center py-16">
        <Loader2 className="size-6 animate-spin text-fieldiq-muted" aria-label="Loading the course" />
      </div>
    ) : failure ? (
      <LoadFailureNotice error={queryFailureError(query)} kind={failure} subject="this course" />
    ) : (
      <p className="px-4 py-6 text-sm text-fieldiq-text" data-testid="course-not-found">
        This course was not found.
      </p>
    );
    content = (
      <>
        <PageHeaderBand eyebrow="Training course" title={query.isLoading ? 'Loading…' : 'Edit Course'} onBack={back} backLabel="Back to courses" />
        <Body>
          <section className={PANEL}>{state}</section>
        </Body>
      </>
    );
  }

  return (
    <div data-testid="course-edit-page" className="animate-fade-in min-h-full bg-fieldiq-dark">
      {content}
    </div>
  );
}
